import fontkit from '@pdf-lib/fontkit';
import { PDFDocument, type PDFFont, type PDFImage, type PDFPage, rgb } from 'pdf-lib';
import type { QuoteFonts } from './fonts.ts';
import type { QuoteModel } from './model.ts';

/** Images for the quote (JPEG bytes; the logo may be PNG). Free images already carry the watermark. */
export interface QuoteImages {
  hero: Uint8Array | null;
  lines: Uint8Array | null;
  logo: Uint8Array | null;
  renders: { label: string; bytes: Uint8Array }[];
}

const MARGIN = 48;
const GRAY = rgb(0.42, 0.42, 0.45);
const INK = rgb(0.09, 0.09, 0.1);
const RULE = rgb(0.85, 0.85, 0.87);

function hexRgb(hex: string) {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return rgb(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
}

function luminance(hex: string): number {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
}

const isPng = (b: Uint8Array) => b[0] === 0x89 && b[1] === 0x50;

/** Greedy word wrap by advance width. */
export function wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const out: string[] = [];
  for (const para of text.split(/\r?\n/)) {
    let line = '';
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(next, size) <= maxWidth || !line) line = next;
      else {
        out.push(line);
        line = word;
      }
    }
    out.push(line);
  }
  return out;
}

/** Flowing layout cursor with automatic page breaks. */
class Flow {
  page!: PDFPage;
  y = 0;
  readonly w: number;
  readonly h: number;
  constructor(
    readonly doc: PDFDocument,
    readonly size: [number, number],
    readonly regular: PDFFont,
    readonly bold: PDFFont,
  ) {
    [this.w, this.h] = size;
    this.newPage();
  }
  get contentW() {
    return this.w - 2 * MARGIN;
  }
  newPage() {
    this.page = this.doc.addPage(this.size);
    this.y = this.h - MARGIN;
  }
  ensure(height: number) {
    if (this.y - height < MARGIN + 24) this.newPage();
  }
  text(
    s: string,
    opts: { size?: number; font?: PDFFont; color?: ReturnType<typeof rgb>; x?: number; gap?: number } = {},
  ) {
    const size = opts.size ?? 10;
    const font = opts.font ?? this.regular;
    for (const line of wrapText(s, font, size, this.contentW - ((opts.x ?? MARGIN) - MARGIN))) {
      this.ensure(size * 1.4);
      this.y -= size * 1.3;
      this.page.drawText(line, { x: opts.x ?? MARGIN, y: this.y, size, font, color: opts.color ?? INK });
    }
    this.y -= opts.gap ?? 0;
  }
  image(img: PDFImage, maxH: number) {
    const scale = Math.min(this.contentW / img.width, maxH / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    this.ensure(h + 8);
    this.y -= h;
    this.page.drawImage(img, { x: MARGIN + (this.contentW - w) / 2, y: this.y, width: w, height: h });
    this.y -= 14;
  }
}

function drawHeader(f: Flow, m: QuoteModel, logo: PDFImage | null) {
  const top = f.y;
  let y = top;
  if (logo) {
    const s = Math.min(140 / logo.width, 48 / logo.height);
    y -= logo.height * s;
    f.page.drawImage(logo, { x: MARGIN, y, width: logo.width * s, height: logo.height * s });
    y -= 8;
  }
  m.business.forEach((line, i) => {
    const size = i === 0 ? 12 : 9;
    y -= size * 1.35;
    f.page.drawText(line, { x: MARGIN, y, size, font: i === 0 ? f.bold : f.regular, color: i === 0 ? INK : GRAY });
  });
  // Right column: "Quote", number, dates.
  const right = f.w - MARGIN;
  let ry = top - 22;
  const title = 'Quote';
  f.page.drawText(title, {
    x: right - f.bold.widthOfTextAtSize(title, 22),
    y: ry,
    size: 22,
    font: f.bold,
    color: hexRgb(m.accent),
  });
  for (const t of [m.number, `Date ${m.date}`, `Valid until ${m.validUntil}`]) {
    ry -= 13;
    f.page.drawText(t, { x: right - f.regular.widthOfTextAtSize(t, 9), y: ry, size: 9, font: f.regular, color: GRAY });
  }
  f.y = Math.min(y, ry) - 12;
  f.page.drawRectangle({ x: MARGIN, y: f.y, width: f.contentW, height: 2, color: hexRgb(m.accent) });
  f.y -= 18;
}

function drawCustomer(f: Flow, m: QuoteModel) {
  f.text('PREPARED FOR', { size: 8, font: f.bold, color: GRAY });
  f.text(m.customer[0] ?? m.projectName, { size: 13, font: f.bold });
  for (const line of m.customer.slice(1)) f.text(line, { size: 9.5, color: GRAY });
  if (m.customer.length) f.text(`Project: ${m.projectName}`, { size: 9.5, color: GRAY });
  f.y -= 10;
}

function drawTable(f: Flow, m: QuoteModel) {
  const cols = {
    swatch: MARGIN,
    name: MARGIN + 18,
    feet: MARGIN + f.contentW * 0.56,
    price: MARGIN + f.contentW * 0.72,
    sub: MARGIN + f.contentW,
  };
  const headH = 20;
  f.ensure(headH + 40);
  f.y -= headH;
  const accent = hexRgb(m.accent);
  f.page.drawRectangle({ x: MARGIN, y: f.y, width: f.contentW, height: headH, color: accent });
  const headInk = luminance(m.accent) > 0.6 ? INK : rgb(1, 1, 1);
  const head = (t: string, x: number, right = false) =>
    f.page.drawText(t, {
      x: right ? x - 6 - f.bold.widthOfTextAtSize(t, 8.5) : x,
      y: f.y + 6.5,
      size: 8.5,
      font: f.bold,
      color: headInk,
    });
  head('Product', cols.name);
  head(m.lines.headers.length, cols.feet);
  head(m.lines.headers.price, cols.price);
  head('Subtotal', cols.sub, true);
  for (const it of m.lines.items) {
    const desc = it.description ? wrapText(it.description, f.regular, 8.5, cols.feet - cols.name - 8) : [];
    const rowH = 16 + desc.length * 11;
    f.ensure(rowH);
    f.y -= rowH;
    const base = f.y + rowH - 13;
    f.page.drawRectangle({ x: cols.swatch + 2, y: base - 1, width: 10, height: 10, color: hexRgb(it.color) });
    f.page.drawText(it.name, { x: cols.name, y: base, size: 10, font: f.bold, color: INK });
    desc.forEach((d, i) => {
      f.page.drawText(d, { x: cols.name, y: base - 11 * (i + 1), size: 8.5, font: f.regular, color: GRAY });
    });
    // The embedded Inter subsets have no "≈" (it would print as a missing-glyph box); "~" reads the same.
    f.page.drawText(it.lengthText.replace('≈', '~'), { x: cols.feet, y: base, size: 10, font: f.regular, color: INK });
    f.page.drawText(it.priceText ?? '—', { x: cols.price, y: base, size: 10, font: f.regular, color: INK });
    const sub = it.subtotalText ?? '—';
    f.page.drawText(sub, {
      x: cols.sub - 6 - f.regular.widthOfTextAtSize(sub, 10),
      y: base,
      size: 10,
      font: f.regular,
      color: INK,
    });
    f.page.drawRectangle({ x: MARGIN, y: f.y, width: f.contentW, height: 0.5, color: RULE });
  }
  const totalRow = (label: string, value: string, size: number) => {
    f.ensure(size * 2);
    f.y -= size * 1.8;
    f.page.drawText(label, { x: cols.price, y: f.y, size, font: f.bold, color: INK });
    f.page.drawText(value, {
      x: cols.sub - 6 - f.bold.widthOfTextAtSize(value, size),
      y: f.y,
      size,
      font: f.bold,
      color: INK,
    });
  };
  if (m.lines.totalText) totalRow('Total', m.lines.totalText, 12);
  if (m.lines.depositText) totalRow('Deposit', m.lines.depositText, 10);
  f.y -= 6;
  if (m.lines.footnote) f.text(m.lines.footnote, { size: 8, color: GRAY, gap: 6 });
}

function drawNotesAndTerms(f: Flow, m: QuoteModel) {
  // Keep each heading on the same page as the first two lines of its text.
  const keepWithNext = (body: string, size: number) =>
    f.ensure(8 + 10 * 1.4 + Math.min(2, wrapText(body, f.regular, size, f.contentW).length) * size * 1.4);
  if (m.notes) {
    keepWithNext(m.notes, 9.5);
    f.y -= 8;
    f.text('Notes', { size: 10, font: f.bold });
    f.text(m.notes, { size: 9.5, gap: 4 });
  }
  keepWithNext(m.terms, 9);
  f.y -= 8;
  f.text('Terms', { size: 10, font: f.bold });
  f.text(m.terms, { size: 9, color: GRAY });
}

/** Builds the quote PDF (pdf-lib). Layouts: classic, modern (full-bleed hero page), minimal (no hero). */
export async function buildQuotePdf(m: QuoteModel, images: QuoteImages, fonts: QuoteFonts): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  doc.setTitle(`Quote ${m.number} – ${m.projectName}`);
  doc.setCreator('Better Lights');
  doc.setProducer('Better Lights (pdf-lib)');
  const regular = await doc.embedFont(fonts.regular, { subset: true });
  const bold = await doc.embedFont(fonts.bold, { subset: true });
  const hero = images.hero ? await doc.embedJpg(images.hero) : null;
  const lines = images.lines ? await doc.embedJpg(images.lines) : null;
  const logo = images.logo ? await (isPng(images.logo) ? doc.embedPng(images.logo) : doc.embedJpg(images.logo)) : null;
  const [W, H] = m.page;

  if (m.template === 'modern' && hero) {
    const cover = doc.addPage(m.page);
    const s = Math.max(W / hero.width, H / hero.height);
    cover.drawImage(hero, {
      x: (W - hero.width * s) / 2,
      y: (H - hero.height * s) / 2,
      width: hero.width * s,
      height: hero.height * s,
    });
    cover.drawRectangle({ x: 0, y: 0, width: W, height: 120, color: rgb(0, 0, 0), opacity: 0.62 });
    cover.drawText(m.business[0] ?? 'Lighting proposal', {
      x: MARGIN,
      y: 72,
      size: 20,
      font: bold,
      color: rgb(1, 1, 1),
    });
    cover.drawText(`${m.projectName} · ${m.number}`, {
      x: MARGIN,
      y: 48,
      size: 11,
      font: regular,
      color: rgb(0.9, 0.9, 0.9),
    });
  }
  const f = new Flow(doc, m.page, regular, bold);
  drawHeader(f, m, logo);
  drawCustomer(f, m);
  if (m.template === 'classic' && hero) f.image(hero, H * 0.36);
  if (lines) f.image(lines, H * (m.template === 'minimal' ? 0.42 : 0.34));
  drawTable(f, m);
  drawNotesAndTerms(f, m);

  if (m.aiRenders.length && images.renders.length) {
    f.newPage();
    f.text('AI renders', { size: 14, font: bold, gap: 6 });
    const maxH = (H - 2 * MARGIN - 40) / images.renders.length - 24;
    for (const r of images.renders) {
      f.text(r.label, { size: 9, font: bold, color: GRAY });
      f.image(await doc.embedJpg(r.bytes), maxH);
    }
  }

  const pages = doc.getPages();
  pages.forEach((p, i) => {
    const label = `Page ${i + 1} of ${pages.length}`;
    p.drawText(label, {
      x: W - MARGIN - regular.widthOfTextAtSize(label, 8),
      y: 24,
      size: 8,
      font: regular,
      color: GRAY,
    });
    if (m.footer) p.drawText(m.footer, { x: MARGIN, y: 24, size: 8, font: regular, color: GRAY });
  });
  return doc.save();
}
